import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, User, Calendar, MapPin, Pill, TestTube, Syringe, 
  Clock, FileText, Building2, Phone, Mail, RefreshCw, Printer,
  ChevronRight, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

export default function PatientHistory() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [examRequests, setExamRequests] = useState([]);
  const [vaccinations, setVaccinations] = useState([]);
  const [healthUnit, setHealthUnit] = useState(null);

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados do paciente
      const patientRes = await axiosInstance.get(`/patients/${patientId}`);
      setPatient(patientRes.data);
      
      // Buscar receitas do paciente
      const prescRes = await axiosInstance.get('/prescriptions');
      const patientPrescriptions = prescRes.data.filter(p => p.patient_id === patientId);
      setPrescriptions(patientPrescriptions);
      
      // Buscar solicitações de exame (se houver endpoint)
      try {
        const examsRes = await axiosInstance.get(`/exam-requests?patient_name=${encodeURIComponent(patientRes.data.nome)}`);
        setExamRequests(examsRes.data || []);
      } catch {
        setExamRequests([]);
      }
      
      // Buscar vacinações do paciente
      try {
        const vaccRes = await axiosInstance.get(`/vaccinations/${patientId}`);
        setVaccinations(vaccRes.data?.vaccines || []);
      } catch {
        setVaccinations([]);
      }
      
      // Buscar unidade de saúde
      if (patientRes.data.unit_id) {
        try {
          const unitsRes = await axiosInstance.get('/health-units');
          const unit = unitsRes.data.find(u => u.id === patientRes.data.unit_id);
          setHealthUnit(unit);
        } catch {
          setHealthUnit(null);
        }
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar histórico do paciente');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate.split('/').reverse().join('-'));
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const isExpiringSoon = (renewalDate) => {
    if (!renewalDate) return false;
    const today = new Date();
    const renewal = new Date(renewalDate);
    const diffDays = Math.ceil((renewal - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (renewalDate) => {
    if (!renewalDate) return false;
    const today = new Date();
    const renewal = new Date(renewalDate);
    return renewal < today;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
        <Navbar />
        <div className="max-w-4xl mx-auto p-4 md:p-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Paciente não encontrado</h2>
              <Button onClick={() => navigate('/')}>Voltar ao Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const age = calculateAge(patient.data_nascimento);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
      <Navbar />
      
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Histórico do Paciente
              </h1>
              <p className="text-muted-foreground text-sm md:text-base">
                Visualização completa do prontuário
              </p>
            </div>
          </div>
          <Button onClick={() => window.print()} variant="outline" className="w-full md:w-auto">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Histórico
          </Button>
        </div>

        {/* Dados do Paciente */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
              <User className="w-5 h-5 text-primary" />
              {patient.nome}
            </CardTitle>
            <CardDescription>Dados cadastrais do paciente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Nascimento</p>
                  <p className="font-medium text-sm truncate">
                    {patient.data_nascimento || 'N/A'}
                    {age && <span className="text-gray-500 ml-1">({age} anos)</span>}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Building2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Unidade</p>
                  <p className="font-medium text-sm truncate">{healthUnit?.nome || 'Sem unidade'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Endereço</p>
                  <p className="font-medium text-sm truncate">{patient.endereco || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="font-medium text-sm truncate">{patient.telefone || 'N/A'}</p>
                </div>
              </div>
            </div>
            
            {/* CPF e CNS */}
            <div className="mt-4 flex flex-wrap gap-2">
              {patient.cpf && (
                <Badge variant="outline" className="text-xs">
                  CPF: {patient.cpf}
                </Badge>
              )}
              {patient.cns && (
                <Badge variant="outline" className="text-xs">
                  CNS: {patient.cns}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                Cadastrado em: {formatDate(patient.data_cadastro)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tabs de Histórico */}
        <Tabs defaultValue="prescriptions" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="prescriptions" className="text-xs md:text-sm">
              <Pill className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Receitas</span>
              <span className="sm:hidden">Rec.</span>
              <Badge variant="secondary" className="ml-1 md:ml-2 text-xs">
                {prescriptions.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="exams" className="text-xs md:text-sm">
              <TestTube className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Exames</span>
              <span className="sm:hidden">Exam.</span>
              <Badge variant="secondary" className="ml-1 md:ml-2 text-xs">
                {examRequests.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="vaccines" className="text-xs md:text-sm">
              <Syringe className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Vacinas</span>
              <span className="sm:hidden">Vac.</span>
              <Badge variant="secondary" className="ml-1 md:ml-2 text-xs">
                {vaccinations.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Receitas */}
          <TabsContent value="prescriptions">
            <div className="space-y-3">
              {prescriptions.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <Pill className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhuma receita encontrada</p>
                  </CardContent>
                </Card>
              ) : (
                prescriptions.map((presc, index) => (
                  <Card key={presc.id || index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge 
                              variant={isExpired(presc.data_validade) ? 'destructive' : 
                                      isExpiringSoon(presc.data_validade) ? 'warning' : 'default'}
                              className="text-xs"
                            >
                              {isExpired(presc.data_validade) ? 'Vencida' : 
                               isExpiringSoon(presc.data_validade) ? 'A vencer' : 'Válida'}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {presc.uso || 'Uso contínuo'}
                            </span>
                          </div>
                          
                          <div className="space-y-1">
                            {(presc.medication_groups || []).slice(0, 2).map((group, gIdx) => (
                              <div key={gIdx} className="text-sm">
                                <span className="text-gray-500 text-xs">Via {group.via}:</span>
                                <span className="ml-2 font-medium truncate">
                                  {group.medicamentos?.slice(0, 2).map(m => m.name).join(', ')}
                                  {group.medicamentos?.length > 2 && '...'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1 text-right flex-shrink-0">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            Criada: {formatDate(presc.data_criacao || presc.created_at)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            Validade: {formatDate(presc.data_validade)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tab: Exames */}
          <TabsContent value="exams">
            <div className="space-y-3">
              {examRequests.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <TestTube className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhuma solicitação de exame encontrada</p>
                  </CardContent>
                </Card>
              ) : (
                examRequests.map((exam, index) => (
                  <Card key={exam.id || index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700">
                              {exam.exams?.length || 0} exame(s)
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatDate(exam.created_at)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-1">
                            {exam.exams?.slice(0, 4).map((examName, eIdx) => (
                              <Badge key={eIdx} variant="secondary" className="text-xs">
                                {examName}
                              </Badge>
                            ))}
                            {exam.exams?.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{exam.exams.length - 4} mais
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {exam.validation_code && (
                          <Badge variant="outline" className="font-mono text-xs">
                            {exam.validation_code}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tab: Vacinas */}
          <TabsContent value="vaccines">
            <div className="space-y-3">
              {vaccinations.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <Syringe className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhuma vacina registrada</p>
                  </CardContent>
                </Card>
              ) : (
                vaccinations.map((vaccine, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">{vaccine.name}</p>
                            <p className="text-sm text-gray-500">
                              {vaccine.doses?.length || 0} dose(s) aplicada(s)
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      
                      {vaccine.doses && vaccine.doses.length > 0 && (
                        <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                          {vaccine.doses.map((dose, dIdx) => (
                            <Badge key={dIdx} variant="outline" className="text-xs">
                              {dose.dose_number}ª dose - {dose.date}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
